/**
 * The icon set: Phosphor regular, named by the job each icon does.
 *
 * The desktop app draws Phosphor (`src/ui/iconfont.cpp`, the regular weight of
 * `@phosphor-icons/web 2.1.1`) and this app drew lucide, which is most of why a
 * screenshot of one did not look like the other — two different hands, in the
 * same places. It is Phosphor here now, from the same package, and the names
 * are `src/ui/iconmap.h`'s: a **role**, not a glyph, because Meet now, the
 * Meetings tab, the call button and the Calls settings page are all
 * `video-camera` and a name that says what it is for survives a change of
 * glyph.
 *
 * `scripts/icon-map.mjs` is that table, `npm run icons` turns it into
 * `icons.generated.ts`, and this file is the door between it and the design
 * system's `Icon`, which asks `window.lucide` for a node and hands whatever
 * comes back to `el.replaceChildren()`. The bundle was written against lucide
 * and asks for lucide's names in a few places of its own, so the map answers
 * those too — each pointed at the glyph the desktop uses for the same job.
 *
 * A name that is not in the map renders nothing at all and throws nothing, so
 * grep the map first when an icon goes blank.
 */

import { GLYPHS, ROLES } from './icons.generated';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** What `iconSet` holds and `createElement` takes: Phosphor's path data. */
export type IconNode = string[];

export const iconSet: Record<string, IconNode> = Object.fromEntries(
  Object.entries(ROLES)
    .map(([name, glyph]) => [name, GLYPHS[glyph]])
    .filter(([, paths]) => paths !== undefined)
);

/**
 * Build the element the design system's `Icon` inserts.
 *
 * It sets `width`, `height` and `stroke-width` on whatever this returns.
 * Phosphor regular is filled rather than stroked, so the stroke width lands on
 * an attribute nothing reads — harmless, and not worth a fork of the bundle to
 * stop. `fill="currentColor"` is what makes an icon take the colour of the text
 * around it, exactly as `icons::pixmap()` tints a glyph in the desktop app.
 */
export function createElement(node: IconNode): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 256 256');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('xmlns', SVG_NS);
  for (const d of node) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
  }
  return svg;
}
